import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Visionneuse en consultation seule.
 *
 * Le serveur ne renvoie jamais le fichier source : chaque page arrive sous
 * forme d'image PNG rendue cote serveur. Cette page-ci ajoute les garde-fous
 * d'interface — pas de menu contextuel, pas de glisser-deposer, pas de
 * raccourcis d'enregistrement ou d'impression, filigrane nominatif.
 *
 * A dire clairement a l'utilisateur : aucune visionneuse web n'empeche une
 * capture d'ecran ou une photo. Le filigrane porte l'identite du lecteur
 * precisement pour que ce qui sortirait de la plateforme reste attribuable.
 */

// Raccourcis d'enregistrement / impression / copie neutralises pendant
// l'affichage. Ils ne protegent pas d'un utilisateur determine, mais ils
// suppriment les gestes reflexes qui exfiltrent un document sans intention.
const BLOCKED_KEYS = new Set(['s', 'p', 'c', 'u']);

export default function DocumentViewer({
  title,          // libellé de la pièce (ex. « Business Plan »)
  subtitle,       // nom de la startup
  watermark,      // identité du lecteur, incrustée sur chaque page
  loadPreview,    // () => Promise<{renderable, reason, pages}>
  pageUrl,        // (page) => url de l'image de la page
  onClose,
}) {
  const [state, setState] = useState({ status: 'loading' });
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [pageSrc, setPageSrc] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const objectUrlRef = useRef(null);

  // --- Chargement de l'aperçu -------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    loadPreview()
      .then(res => { if (!cancelled) setState({ status: 'ready', ...res }); })
      .catch(err => { if (!cancelled) setState({ status: 'error', reason: err.message }); });
    return () => { cancelled = true; };
  }, [loadPreview]);

  // --- Chargement d'une page --------------------------------------------------
  // L'image est recuperee par fetch (en-tete Authorization obligatoire) puis
  // convertie en URL d'objet, revoquee des que la page change : aucune image ne
  // reste accessible apres la fermeture.
  useEffect(() => {
    if (state.status !== 'ready' || !state.renderable) return undefined;
    let cancelled = false;
    setPageLoading(true);
    fetch(pageUrl(page), {
      headers: { Authorization: `Bearer ${localStorage.getItem('isi_token')}` },
    })
      .then(async res => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Page indisponible (HTTP ${res.status})`);
        }
        return res.blob();
      })
      .then(blob => {
        if (cancelled) return;
        if (objectUrlRef.current) window.URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = window.URL.createObjectURL(blob);
        setPageSrc(objectUrlRef.current);
      })
      .catch(err => { if (!cancelled) setState(s => ({ ...s, pageError: err.message })); })
      .finally(() => { if (!cancelled) setPageLoading(false); });
    return () => { cancelled = true; };
  }, [state.status, state.renderable, page, pageUrl]);

  useEffect(() => () => {
    if (objectUrlRef.current) window.URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  // --- Garde-fous clavier -----------------------------------------------------
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if ((e.ctrlKey || e.metaKey) && BLOCKED_KEYS.has(e.key.toLowerCase())) {
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowRight') setPage(p => Math.min(p + 1, state.pages || 1));
    if (e.key === 'ArrowLeft') setPage(p => Math.max(p - 1, 1));
  }, [onClose, state.pages]);

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown, true);
    // L'impression rendrait le filigrane inutile et produirait un PDF du
    // document : on masque la visionneuse dans la feuille de style d'impression.
    const style = document.createElement('style');
    style.textContent = '@media print { #doc-viewer-root { display: none !important; } body { visibility: hidden !important; } }';
    document.head.appendChild(style);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      style.remove();
      document.body.style.overflow = prevOverflow;
    };
  }, [onKeyDown]);

  const block = (e) => { e.preventDefault(); return false; };
  const pages = state.pages || 0;

  return (
    <div id="doc-viewer-root"
      onContextMenu={block}
      style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15,17,32,0.92)',
        display: 'flex', flexDirection: 'column', userSelect: 'none', WebkitUserSelect: 'none' }}>

      {/* ---- Barre de titre ---- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
        background: '#1B1E38', color: 'white', flexShrink: 0, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 140px', minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, opacity: 0.7 }}>{subtitle}</div>}
        </div>

        {state.status === 'ready' && state.renderable && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                style={navBtn(page <= 1)}>‹</button>
              <span style={{ fontSize: 13, minWidth: 92, textAlign: 'center' }}>
                Page {page} / {pages}
              </span>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
                style={navBtn(page >= pages)}>›</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                disabled={zoom <= 0.5} style={navBtn(zoom <= 0.5)}>−</button>
              <span style={{ fontSize: 13, minWidth: 48, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))}
                disabled={zoom >= 3} style={navBtn(zoom >= 3)}>+</button>
            </div>
          </>
        )}

        <button onClick={onClose}
          style={{ padding: '9px 18px', minHeight: 40, borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)',
            background: 'transparent', color: 'white', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 13, fontWeight: 600 }}>
          Fermer
        </button>
      </div>

      {/* ---- Bandeau d'information ---- */}
      <div style={{ padding: '7px 20px', background: '#2A2F52', color: 'rgba(255,255,255,0.75)',
        fontSize: 11.5, flexShrink: 0 }}>
        Consultation seule — ce document ne peut être ni téléchargé ni imprimé depuis la plateforme.
        Chaque ouverture est enregistrée.
      </div>

      {/* ---- Zone de rendu ---- */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'clamp(8px, 2vw, 24px)', display: 'flex',
        justifyContent: 'center', alignItems: 'flex-start' }}>

        {state.status === 'loading' && (
          <div style={{ color: 'white', marginTop: 60, fontSize: 14 }}>Préparation du document…</div>
        )}

        {state.status === 'error' && (
          <div style={message('#FFEBEE', '#C62828')}>{state.reason}</div>
        )}

        {state.status === 'ready' && !state.renderable && (
          <div style={message('#FFF8E1', '#8D6E20')}>{state.reason}</div>
        )}

        {state.pageError && (
          <div style={message('#FFEBEE', '#C62828')}>{state.pageError}</div>
        )}

        {/* maxWidth 100% : a 100 %, la page s'ajuste a la largeur disponible —
            sans quoi une image rendue a 1200 px deborde l'ecran d'un telephone
            des l'ouverture. Au-dela de 100 %, le debordement est voulu : c'est
            le zoom, et le conteneur defile. */}
        {state.status === 'ready' && state.renderable && !state.pageError && (
          <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
            {pageSrc && (
              <img src={pageSrc} alt={`${title} — page ${page}`}
                onContextMenu={block} onDragStart={block}
                style={{ display: 'block', width: `${zoom * 100}%`,
                  maxWidth: zoom <= 1 ? '100%' : 'none',
                  background: 'white', borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)', pointerEvents: 'none' }} />
            )}
            {pageLoading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'center', background: 'rgba(255,255,255,0.6)', color: '#1B1E38',
                fontSize: 13 }}>
                Chargement de la page…
              </div>
            )}
            {/* Filigrane : rend une capture d'ecran attribuable. Motif SVG
                REPETE plutot qu'un bloc centre — sinon une capture du haut ou
                d'un bord de page ne porterait aucune marque. */}
            {watermark && pageSrc && (
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: `url("${watermarkTile(watermark)}")`,
                backgroundRepeat: 'repeat' }} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Tuile SVG du filigrane, encodee en data URI et repetee en fond : le motif
// couvre alors toute la page quelle que soit sa taille, y compris les bords.
function watermarkTile(text) {
  const safe = String(text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="420" height="220">
    <text x="210" y="110" transform="rotate(-30 210 110)"
      font-family="Arial, Helvetica, sans-serif" font-size="17" font-weight="700"
      fill="rgba(110,110,145,0.22)" text-anchor="middle">${safe}</text>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// Cible tactile : 40 px de cote, pour rester atteignable au doigt sur tablette.
const navBtn = (disabled) => ({
  minWidth: 40, minHeight: 40, padding: '4px 12px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.25)',
  background: 'transparent', color: 'white', cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: 'inherit', fontSize: 17, opacity: disabled ? 0.35 : 1, lineHeight: 1.2,
});

const message = (bg, color) => ({
  marginTop: 60, maxWidth: 520, padding: '16px 20px', borderRadius: 10,
  background: bg, color, fontSize: 13.5, lineHeight: 1.6, textAlign: 'center',
});
